// ABOUTME: Route and URL utility functions for handling paths and API endpoints
// ABOUTME: Provides helpers for route pattern matching and constructing full API URLs

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

export const getFullApiUrl = (relativePath?: string | null): string => {
  if (!relativePath) {
    return '';
  }

  // If it's already a full URL, return as-is
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }

  // If it's a relative API path, prepend the base URL
  if (relativePath.startsWith('/api/')) {
    return `${API_BASE_URL}${relativePath}`;
  }

  // For other relative paths, assume they're API paths
  return `${API_BASE_URL}${relativePath.startsWith('/') ? '' : '/'}${relativePath}`;
};

/**
 * Check if the current path matches any pattern in the suppressed routes list
 *
 * Supports:
 * - Exact matching: /setup matches /setup
 * - Base path matching: /setup matches /setup/step1 (startswith behavior)
 * - Simple wildcards: /admin/* matches /admin/users but NOT /admin/users/123
 *
 * Case-sensitive matching.
 *
 * @param currentPath - The current route path to check
 * @param suppressedRoutes - Array of route patterns to match against
 * @returns true if currentPath matches any suppressed route pattern
 */
export const matchesRoutePattern = (
  currentPath: string,
  suppressedRoutes: string[]
): boolean => {
  // Normalize trailing slashes for consistent comparison
  const normalizedCurrent = currentPath.endsWith('/') && currentPath !== '/'
    ? currentPath.slice(0, -1)
    : currentPath;

  for (const pattern of suppressedRoutes) {
    const normalizedPattern = pattern.endsWith('/') && pattern !== '/'
      ? pattern.slice(0, -1)
      : pattern;

    // Exact match
    if (normalizedCurrent === normalizedPattern) {
      return true;
    }

    // Wildcard match - simple single-level wildcard
    if (normalizedPattern.endsWith('/*')) {
      const basePattern = normalizedPattern.slice(0, -2); // Remove /*

      // Check if current path starts with base and has exactly one more segment
      if (normalizedCurrent.startsWith(basePattern + '/')) {
        const remainder = normalizedCurrent.substring(basePattern.length + 1);
        // Single-level wildcard: should not contain additional slashes
        if (!remainder.includes('/')) {
          return true;
        }
      }
    }

    // Base path matching (startswith) - /setup matches /setup/anything
    if (normalizedCurrent.startsWith(normalizedPattern + '/')) {
      return true;
    }
  }

  return false;
};
